import { AppConfig } from "../config/AppConfig.js";
import { ApiCache } from "./ApiCache.js";

export class ApiService {
  static baseUrl = (AppConfig.api.isLocal) ? AppConfig.api.localUrl : AppConfig.api.baseUrl;
  static timeout = AppConfig.api.timeout;
  static retryAttempts = AppConfig.api.retryAttempts;

  static async post(endpoint, data, token = null, options = {}) {
    return this.makeRequest("POST", `${this.baseUrl}/${endpoint}`, data, token, options);
  }

  static async get(endpoint, token = null, options = {}) {
    return this.makeRequest("GET", `${this.baseUrl}/${endpoint}`, null, token, options);
  }

  // es igual al de post, pero se deja para diferenciar semánticamente
	static async postFormData(endpoint, formData, token = null) {
		return this.makeRequest("POST", `${this.baseUrl}/${endpoint}`, formData, token);
	}

  static async postBlob(endpoint, data, token = null) {
    const url = `${this.baseUrl}/${endpoint}`;
    const headers = {};
    if (token) headers["x-token"] = token;
    if (data) headers["Content-Type"] = "application/json";

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: data ? JSON.stringify(data) : null,
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.blob();
  }

  static invalidate(endpointContains = "", token = null) {
    ApiCache.invalidate({ urlContains: `${this.baseUrl}/${endpointContains}`, token });
  }

  static async makeRequest(method, url, data = null, token = null, options = {}) {
    const {
      cacheTiempo = "false",            // false | "30s" | "1d" | 60000
      cachePersistent = true,   // guarda también en localStorage
      forceRefresh = false,     // ignora cache
      dedupe = true,            // evita requests simultáneas iguales
    } = options;

	const cache = (AppConfig.api.cacheEnabled) ? cacheTiempo : false;

    const ttlMs = ApiCache.parseTTL(cache);

    const cacheKey = ApiCache.buildKey({ method, url, data, token });
    const canCache = ApiCache.canCache({ ttlMs, data });

    // 1) cache hit
    if (canCache && !forceRefresh) {
      const cached = ApiCache.read(cacheKey);
      if (cached !== null) return cached;
    }

    // 2) dedupe in-flight
    if (dedupe) {
      const inflight = ApiCache.getInFlight(cacheKey);
      if (inflight) return inflight;
    }

    // 3) request con retries
    const requestPromise = (async () => {
      let lastError;

      for (let attempt = 1; attempt <= this.retryAttempts; attempt++) {
        try {
          const headers = {};

          if (token) headers["x-token"] = token;

          let bodyToSend = data;

          if (data && !(data instanceof FormData)) {
            headers["Content-Type"] = "application/json";
            bodyToSend = JSON.stringify(data);
          }

          const response = await fetch(url, {
            method,
            headers,
            body: bodyToSend ? bodyToSend : null,
          });

          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

          const json = await response.json();

          // guardar cache
          if (canCache) {
            ApiCache.write(cacheKey, json, ttlMs, cachePersistent);
          }

          return json;

        } catch (error) {
          lastError = error;

          if (AppConfig.features?.debug) {
            console.warn(`API request attempt ${attempt} failed:`, error);
          }

          if (attempt === this.retryAttempts) break;

          await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
        }
      }

      console.error("API Error after all retries:", lastError);
      throw lastError;
    })();

    if (dedupe) ApiCache.setInFlight(cacheKey, requestPromise);

    try {
      return await requestPromise;
    } finally {
      if (dedupe) ApiCache.clearInFlight(cacheKey);
    }
  }
}
